/**
 * Content Rules Center Types
 * Types for the Content Rule Center feature APIs
 */

// Severity levels for content rules
export type RuleSeverity = SeverityCode;

// Tag types for content rules
export interface RuleTag {
  type: 'country' | 'brand' | 'audience' | 'channel' | 'category';
  value: string;
}

// Nested type definitions for content rules
export interface RuleType {
  code: string;
  name: string;
  description: string;
}

export interface RuleCategory {
  code: string;
  name: string;
  description: string;
}

export interface RuleSeverityObj {
  code: string;
  name: string;
  description: string;
}

// Individual content rule
export interface ContentRule {
  id: string;
  name: string;
  description: string;
  version: number;
  previousVersionId: string | null;
  targetAudience: string;
  source: string;
  status: string;
  tags: string[];
  enabled: boolean;
  lastUpdatedDate: string;
  createdBy: string;
  updatedBy: string;
  type: RuleType;
  category: RuleCategory;
  severity: RuleSeverityObj;
}

// Category metadata response
export interface ContentRuleCategory {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  ruleCount: number;
}

export interface CategoriesResponse {
  success: boolean;
  data: {
    items: ContentRuleCategory[];
  };
}

// Severity-wise summary response
export enum SeverityCode {
  INFO = 'INFO',
  WARNING = 'WARNING',
  BLOCKER = 'BLOCKER',
}

export interface SeveritySummaryItem {
  severityName: string;
  severityCode: SeverityCode;
  ruleCount: number;
}

export interface SeveritySummaryResponse {
  success: boolean;
  message: string;
  errors: string[];
  data: {
    totalRuleCount: string;
    severityRuleCounts: SeveritySummaryItem[];
  };
}

// Category-wise summary response
export interface SeverityRuleCount {
  severityName: string;
  severityCode: SeverityCode;
  ruleCount: number;
}

export interface CategoryRuleCount {
  categoryName: string;
  categoryCode: string;
  severityRuleCounts: SeverityRuleCount[];
}

export interface CategorySummaryResponse {
  success: boolean;
  message: string;
  errors: string[];
  data: {
    categoryRuleCounts: CategoryRuleCount[];
  };
}

// Content rules list response (paginated)
export interface ContentRulesResponse {
  success: boolean;
  message: string;
  errors: string[];
  data: {
    rules: ContentRule[];
    pagination: {
      total: number;
      totalPages: number;
      page: number;
      pageSize: number;
      nextPage: number | null;
      previousPage: number | null;
    };
  };
}

// Request parameters for fetching content rules
export interface GetContentRulesParams {
  countries?: string;
  brands?: string;
  audience?: string;
  channels?: string;
  categories?: string;
  page?: number;
  pageSize?: number;
}

// Request parameters for fetching category-wise summary
export interface GetCategorySummaryParams {
  countries?: string;
  brands?: string;
  audience?: string;
  channels?: string;
  categories?: string;
}

// Filter state for Content Rules Center UI
export interface ContentRulesFilters {
  country: string | null;
  brand: string | null;
  audience: string | null;
  channel: string | null;
  category: string | null;
}
